"use client";

import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
  useColorModeValue,
  Text,
  Flex,
  HStack,
  Link,
} from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import type { Edge } from "./types.ts";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface EdgePropertiesDialogProps {
  edge: Edge;
  onClose: () => void;
  onUpdate: (edge: Edge) => void;
  onDelete: (edgeId: string) => void;
}

export const EdgePropertiesDialog: React.FC<EdgePropertiesDialogProps> = ({
  edge,
  onClose,
  onUpdate,
  onDelete,
}) => {
  // Initialize with default schema structure if parameters is empty
  const defaultParameters = {
    type: "object",
    properties: {},
  };

  const initialParameters = edge.data?.parameters || defaultParameters;

  const [label, setLabel] = useState(edge.label || "");
  const [parametersJson, setParametersJson] = useState(
    JSON.stringify(initialParameters, null, 2)
  );

  const [jsonError, setJsonError] = useState<string | null>(null);

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleSave = () => {
    try {
      let parameters = defaultParameters;

      if (parametersJson) {
        parameters = JSON.parse(parametersJson);

        // Ensure the parameters object has the required structure
        if (!parameters.type) {
          parameters.type = "object";
        }

        if (!parameters.properties) {
          parameters.properties = {};
        }
      }

      setJsonError(null);

      const updatedEdge: Edge = {
        ...edge,
        label,
        data: {
          ...edge.data,
          parameters,
        },
      };
      onUpdate(updatedEdge);
      onClose();
    } catch (error) {
      setJsonError("Invalid JSON format");
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(parametersJson);
      setParametersJson(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (error) {
      setJsonError("Invalid JSON format");
    }
  };

  const loadExample = (exampleNum: number) => {
    let exampleJson = {};

    if (exampleNum === 1) {
      exampleJson = {
        type: "object",
        properties: {
          customer_name: {
            type: "string",
            description: "The name of the customer",
          },
        },
        required: ["customer_name"],
      };
    } else if (exampleNum === 2) {
      exampleJson = {
        type: "object",
        properties: {
          appointment_date: {
            type: "string",
            description: "The date of the appointment",
          },
          appointment_time: {
            type: "string",
            description: "The time of the appointment",
          },
        },
        required: ["appointment_date", "appointment_time"],
      };
    } else if (exampleNum === 3) {
      exampleJson = {
        type: "object",
        properties: {
          issue_type: {
            type: "string",
            description: "The type of issue reported",
          },
          urgency: {
            type: "string",
            description: "How urgent the issue is",
            enum: ["low", "medium", "high"],
          },
        },
      };
    }

    setParametersJson(JSON.stringify(exampleJson, null, 2));
    setJsonError(null);
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        color={isDark ? "whiteAlpha.900" : "gray.800"}
      >
        <ModalHeader>Agent edge</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Path Condition</FormLabel>
              <Text
                fontSize="xs"
                color={isDark ? "gray.300" : "gray.600"}
                mb={2}
              >
                Describe the condition that triggers the transition to the next
                state
              </Text>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="The condition that triggers the transition to the next state"
                border="1px solid"
                _placeholder={{
                  color: isDark ? "gray.400" : "gray.600",
                }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{
                  borderColor: isDark ? "gray.500" : "gray.400",
                }}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
                variant="outline"
              />
            </FormControl>

            <FormControl isInvalid={!!jsonError}>
              <FormLabel>Store Call Info as Variables (Optional)</FormLabel>
              <Text
                fontSize="xs"
                color={isDark ? "gray.300" : "gray.600"}
                mb={2}
              >
                Convert information from the previous node into variables for
                use in future nodes. This should be the JSON schema that defines
                the format in which the LLM will return.
                {/* {" "} */}
                {/* <Link
                  href="https://docs.retellai.com/build/write-multi-prompt"
                  color={isDark ? "gray.300" : "gray.600"}
                  isExternal
                >
                  (Learn more) <ExternalLinkIcon mx="2px" />
                </Link> */}
              </Text>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                value={parametersJson}
                onChange={(e) => setParametersJson(e.target.value)}
                placeholder="Enter JSON Schema here..."
                minHeight="200px"
                fontFamily="monospace"
                border="1px solid"
                _placeholder={{
                  color: isDark ? "gray.400" : "gray.600",
                }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{
                  borderColor: isDark ? "gray.500" : "gray.400",
                }}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
                variant="outline"
              />
              {jsonError && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {jsonError}
                </Text>
              )}

              <HStack mt={3} spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(1)}
                >
                  example 1
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(2)}
                >
                  example 2
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  background={isDark ? "gray.700" : "gray.200"}
                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                  onClick={() => loadExample(3)}
                >
                  example 3
                </Button>
              </HStack>

              <Button
                mt={3}
                width="100%"
                colorScheme="gray"
                variant="outline"
                onClick={formatJson}
              >
                Format JSON
              </Button>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Flex width="100%" justifyContent="space-between">
            <Box>
              <Button
                colorScheme="red"
                color={isDark ? "red.300" : "red.600"}
                variant="ghost"
                leftIcon={<Trash2 size={16} />}
                onClick={() => {
                  onDelete(edge.id);
                  onClose();
                }}
              >
                Delete
              </Button>
            </Box>

            <Box>
              <Button
                colorScheme={isDark ? "black" : "white"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                variant="ghost"
                mr={3}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                background={isDark ? "gray.700" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
